import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { MessageCircle, X, Send, ShieldCheck, Loader2, User, Sparkles, BookOpen, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// All UI strings in 8 languages
const i18n = {
  en: {
    bubbleAria: 'Open chat',
    title: 'Skopelos Concierge',
    subtitle: 'Ask me about the property',
    consentTitle: 'Before we begin',
    consentBody: 'I\'m an AI concierge here to answer questions about the Skopelos Cottages. To chat, I\'ll process your messages temporarily to provide responses. No personal data is required unless you choose to share it.',
    consentDetail: 'Chat messages are stored for up to 30 days. If you choose to leave your contact details, those are kept for up to 18 months. We never sell your data.',
    consentPolicy: 'Read our Privacy Policy',
    consentAccept: 'Start chatting',
    consentDecline: 'Not now',
    inputPlaceholder: 'Type your message…',
    send: 'Send',
    leadTitle: 'Would you like the owner to follow up?',
    leadBody: 'Leave your details and the owner will reach out personally. Optional — keep chatting anytime.',
    leadName: 'Your name',
    leadEmail: 'Email address',
    leadNote: 'Anything specific? (optional)',
    leadSubmit: 'Send to owner',
    leadCancel: 'Maybe later',
    leadSuccess: 'Thank you — the owner will be in touch.',
    askContact: 'Leave your details',
    poweredBy: 'AI replies — not legal advice',
    errorGeneric: 'Something went wrong. Please try again.',
    ended: 'This conversation has ended. Please use the contact form for further questions.',
    quickReplies: [
      "What's nearby?",
      'Show me the map',
      'View the photos',
      'Is the property legally clear?',
    ],
    guideChip: '📘 Get the Buyer\'s Guide (PDF)',
    guideCardTitle: 'Want the full picture in one PDF?',
    guideCardBody: 'Get our free Buyer\'s Quick Guide — property facts, distances, legal status & how to view.',
    guideFormTitle: 'Buyer\'s Quick Guide',
    guideFormBody: 'Enter your email and we\'ll send you the PDF. You\'ll also be able to download it instantly.',
    guideFormName: 'Your name (optional)',
    guideFormEmail: 'Email address',
    guideFormSubmit: 'Send me the guide',
    guideFormCancel: 'Cancel',
    guideSuccess: 'Sent! Check your inbox — the PDF is also downloading now.',
  },
  gr: {
    bubbleAria: 'Άνοιγμα συνομιλίας',
    title: 'Σύμβουλος Σκοπέλου',
    subtitle: 'Ρωτήστε με για το ακίνητο',
    consentTitle: 'Πριν ξεκινήσουμε',
    consentBody: 'Είμαι AI σύμβουλος για να απαντήσω ερωτήσεις σχετικά με τα Εξοχικά της Σκοπέλου. Τα μηνύματά σας επεξεργάζονται προσωρινά για να σας απαντήσω.',
    consentDetail: 'Τα μηνύματα chat αποθηκεύονται έως 30 ημέρες. Εάν αφήσετε τα στοιχεία επικοινωνίας σας, αυτά διατηρούνται έως 18 μήνες. Δεν πωλούμε ποτέ τα δεδομένα σας.',
    consentPolicy: 'Διαβάστε την Πολιτική Απορρήτου',
    consentAccept: 'Έναρξη συνομιλίας',
    consentDecline: 'Όχι τώρα',
    inputPlaceholder: 'Γράψτε το μήνυμά σας…',
    send: 'Αποστολή',
    leadTitle: 'Θέλετε να επικοινωνήσει μαζί σας ο ιδιοκτήτης;',
    leadBody: 'Αφήστε τα στοιχεία σας και ο ιδιοκτήτης θα σας απαντήσει προσωπικά. Προαιρετικό.',
    leadName: 'Όνομα',
    leadEmail: 'Email',
    leadNote: 'Κάτι συγκεκριμένο; (προαιρετικό)',
    leadSubmit: 'Αποστολή',
    leadCancel: 'Αργότερα',
    leadSuccess: 'Ευχαριστούμε — ο ιδιοκτήτης θα επικοινωνήσει.',
    askContact: 'Αφήστε στοιχεία',
    poweredBy: 'Απαντήσεις AI — όχι νομική συμβουλή',
    errorGeneric: 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.',
    ended: 'Η συνομιλία τερματίστηκε. Χρησιμοποιήστε τη φόρμα επικοινωνίας για περισσότερα.',
    quickReplies: [
      'Τι έχει κοντά;',
      'Δείξε μου τον χάρτη',
      'Δείξε φωτογραφίες',
      'Είναι νομικά καθαρό;',
    ],
    guideChip: '📘 Λάβετε τον Οδηγό Αγοραστή (PDF)',
    guideCardTitle: 'Θέλετε όλα τα στοιχεία σε ένα PDF;',
    guideCardBody: 'Λάβετε δωρεάν τον Σύντομο Οδηγό Αγοραστή — στοιχεία, αποστάσεις, νομικά & πώς να κλείσετε επίσκεψη.',
    guideFormTitle: 'Σύντομος Οδηγός Αγοραστή',
    guideFormBody: 'Δώστε email και θα σας στείλουμε το PDF. Θα κατεβεί και αμέσως.',
    guideFormName: 'Όνομα (προαιρετικό)',
    guideFormEmail: 'Email',
    guideFormSubmit: 'Στείλε μου τον οδηγό',
    guideFormCancel: 'Άκυρο',
    guideSuccess: 'Στάλθηκε! Ελέγξτε τα email σας — το PDF κατεβαίνει επίσης τώρα.',
  },
  de: {
    bubbleAria: 'Chat öffnen',
    title: 'Skopelos Concierge',
    subtitle: 'Fragen Sie mich zur Immobilie',
    consentTitle: 'Bevor wir beginnen',
    consentBody: 'Ich bin ein KI-Concierge für die Skopelos Cottages. Ihre Nachrichten werden temporär verarbeitet, um Antworten zu liefern.',
    consentDetail: 'Chat-Nachrichten werden bis zu 30 Tage gespeichert. Wenn Sie Ihre Kontaktdaten hinterlassen, werden diese bis zu 18 Monate aufbewahrt. Wir verkaufen Ihre Daten nie.',
    consentPolicy: 'Datenschutzerklärung lesen',
    consentAccept: 'Chat starten',
    consentDecline: 'Nicht jetzt',
    inputPlaceholder: 'Ihre Nachricht…',
    send: 'Senden',
    leadTitle: 'Soll sich der Eigentümer melden?',
    leadBody: 'Hinterlassen Sie Ihre Daten — der Eigentümer meldet sich persönlich.',
    leadName: 'Ihr Name',
    leadEmail: 'E-Mail-Adresse',
    leadNote: 'Etwas Bestimmtes? (optional)',
    leadSubmit: 'An Eigentümer senden',
    leadCancel: 'Später',
    leadSuccess: 'Danke — der Eigentümer meldet sich.',
    askContact: 'Daten hinterlassen',
    poweredBy: 'KI-Antworten — keine Rechtsberatung',
    errorGeneric: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    ended: 'Das Gespräch ist beendet. Bitte nutzen Sie das Kontaktformular.',
    quickReplies: [
      'Was ist in der Nähe?',
      'Karte zeigen',
      'Fotos ansehen',
      'Ist das Eigentum rechtlich klar?',
    ],
    guideChip: '📘 Käufer-Leitfaden (PDF) erhalten',
    guideCardTitle: 'Alle Fakten in einem PDF?',
    guideCardBody: 'Holen Sie sich unseren kostenlosen Käufer-Schnellleitfaden — Daten, Entfernungen, Recht & Besichtigung.',
    guideFormTitle: 'Käufer-Schnellleitfaden',
    guideFormBody: 'Geben Sie Ihre E-Mail an, wir senden Ihnen das PDF. Sie können es auch sofort herunterladen.',
    guideFormName: 'Ihr Name (optional)',
    guideFormEmail: 'E-Mail-Adresse',
    guideFormSubmit: 'Leitfaden senden',
    guideFormCancel: 'Abbrechen',
    guideSuccess: 'Gesendet! Prüfen Sie Ihren Posteingang — das PDF wird auch jetzt heruntergeladen.',
  },
  fr: {
    bubbleAria: 'Ouvrir le chat',
    title: 'Concierge Skopelos',
    subtitle: 'Posez-moi vos questions',
    consentTitle: 'Avant de commencer',
    consentBody: 'Je suis un concierge IA pour les Cottages de Skopelos. Vos messages sont traités temporairement pour générer des réponses.',
    consentDetail: 'Les messages sont conservés jusqu\'à 30 jours. Si vous laissez vos coordonnées, elles sont conservées jusqu\'à 18 mois. Nous ne vendons jamais vos données.',
    consentPolicy: 'Lire la Politique de Confidentialité',
    consentAccept: 'Commencer',
    consentDecline: 'Pas maintenant',
    inputPlaceholder: 'Votre message…',
    send: 'Envoyer',
    leadTitle: 'Souhaitez-vous être recontacté ?',
    leadBody: 'Laissez vos coordonnées — le propriétaire vous contactera personnellement.',
    leadName: 'Votre nom',
    leadEmail: 'Adresse email',
    leadNote: 'Quelque chose de précis ? (facultatif)',
    leadSubmit: 'Envoyer',
    leadCancel: 'Plus tard',
    leadSuccess: 'Merci — le propriétaire vous contactera.',
    askContact: 'Laisser mes coordonnées',
    poweredBy: 'Réponses IA — pas un conseil juridique',
    errorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
    ended: 'Cette conversation est terminée. Utilisez le formulaire de contact.',
    quickReplies: [
      "Qu'y a-t-il à proximité ?",
      'Voir la carte',
      'Voir les photos',
      'La propriété est-elle en règle ?',
    ],
    guideChip: '📘 Recevoir le Guide Acheteur (PDF)',
    guideCardTitle: 'Tous les détails en un seul PDF ?',
    guideCardBody: 'Recevez notre Guide Acheteur gratuit — faits, distances, statut légal & comment visiter.',
    guideFormTitle: 'Guide Acheteur Rapide',
    guideFormBody: 'Indiquez votre email — nous vous envoyons le PDF. Téléchargement immédiat également.',
    guideFormName: 'Votre nom (facultatif)',
    guideFormEmail: 'Adresse email',
    guideFormSubmit: 'Envoyer le guide',
    guideFormCancel: 'Annuler',
    guideSuccess: 'Envoyé ! Vérifiez votre boîte — le PDF se télécharge aussi maintenant.',
  },
  he: {
    bubbleAria: 'פתח צ\'אט',
    title: 'קונסיירז\' סקופלוס',
    subtitle: 'שאל אותי על הנכס',
    consentTitle: 'לפני שנתחיל',
    consentBody: 'אני קונסיירז\' AI עבור הקוטג\'ים בסקופלוס. ההודעות שלך מעובדות זמנית כדי לספק תשובות.',
    consentDetail: 'הודעות צ\'אט נשמרות עד 30 ימים. אם תשאיר את פרטי הקשר שלך, הם נשמרים עד 18 חודשים. אנו לעולם לא מוכרים את הנתונים שלך.',
    consentPolicy: 'קרא את מדיניות הפרטיות',
    consentAccept: 'התחל שיחה',
    consentDecline: 'לא עכשיו',
    inputPlaceholder: 'ההודעה שלך…',
    send: 'שלח',
    leadTitle: 'רוצה שהבעלים יחזור אליך?',
    leadBody: 'השאר פרטים והבעלים יצור איתך קשר אישית.',
    leadName: 'שמך',
    leadEmail: 'כתובת אימייל',
    leadNote: 'משהו ספציפי? (אופציונלי)',
    leadSubmit: 'שלח לבעלים',
    leadCancel: 'אולי אחר כך',
    leadSuccess: 'תודה — הבעלים יחזור אליך.',
    askContact: 'השאר פרטים',
    poweredBy: 'תשובות AI — לא ייעוץ משפטי',
    errorGeneric: 'משהו השתבש. נסה שוב.',
    ended: 'השיחה הסתיימה. אנא השתמש בטופס הקשר.',
    quickReplies: [
      'מה יש בסביבה?',
      'הראה לי את המפה',
      'הצג תמונות',
      'האם הנכס חוקי?',
    ],
    guideChip: '📘 קבל את מדריך הקונה (PDF)',
    guideCardTitle: 'רוצה את כל התמונה ב-PDF אחד?',
    guideCardBody: 'קבל את מדריך הקונה החינמי שלנו — עובדות, מרחקים, מצב חוקי וכיצד לקבוע צפייה.',
    guideFormTitle: 'מדריך קונה מהיר',
    guideFormBody: 'הזן את האימייל שלך — נשלח לך את ה-PDF. גם להורדה מיידית.',
    guideFormName: 'שמך (אופציונלי)',
    guideFormEmail: 'כתובת אימייל',
    guideFormSubmit: 'שלח לי את המדריך',
    guideFormCancel: 'ביטול',
    guideSuccess: 'נשלח! בדוק את תיבת הדואר — ה-PDF גם יורד עכשיו.',
  },
  zh: {
    bubbleAria: '打开聊天',
    title: '斯科派洛斯礼宾',
    subtitle: '关于房产的问题请问我',
    consentTitle: '开始之前',
    consentBody: '我是斯科派洛斯石屋的 AI 礼宾。您的消息将被临时处理以提供回复。',
    consentDetail: '聊天消息保留最多 30 天。如您留下联系方式，将保留最多 18 个月。我们绝不出售您的数据。',
    consentPolicy: '阅读隐私政策',
    consentAccept: '开始聊天',
    consentDecline: '暂不',
    inputPlaceholder: '请输入消息…',
    send: '发送',
    leadTitle: '希望业主回复您吗？',
    leadBody: '留下联系方式，业主将亲自联系您。',
    leadName: '您的姓名',
    leadEmail: '电子邮箱',
    leadNote: '有具体问题吗？（可选）',
    leadSubmit: '发送给业主',
    leadCancel: '稍后',
    leadSuccess: '谢谢 — 业主将与您联系。',
    askContact: '留下联系方式',
    poweredBy: 'AI 回复 — 非法律建议',
    errorGeneric: '出错了，请重试。',
    ended: '对话已结束。请使用联系表。',
    quickReplies: [
      '附近有什么？',
      '查看地图',
      '查看照片',
      '产权是否清晰？',
    ],
    guideChip: '📘 获取购房指南 (PDF)',
    guideCardTitle: '想要完整的 PDF 资料吗？',
    guideCardBody: '免费获取我们的购房快速指南 — 房产信息、距离、法律状况和看房方式。',
    guideFormTitle: '购房快速指南',
    guideFormBody: '输入您的邮箱，我们会把 PDF 发给您。同时还会立即下载。',
    guideFormName: '您的姓名（可选）',
    guideFormEmail: '电子邮箱',
    guideFormSubmit: '发送指南',
    guideFormCancel: '取消',
    guideSuccess: '已发送！请查看邮箱 — PDF 也在下载中。',
  },
  ar: {
    bubbleAria: 'فتح الدردشة',
    title: 'كونسيرج سكوبيلوس',
    subtitle: 'اسألني عن العقار',
    consentTitle: 'قبل أن نبدأ',
    consentBody: 'أنا كونسيرج ذكي لعقار سكوبيلوس. تتم معالجة رسائلك مؤقتا لتقديم الردود.',
    consentDetail: 'يتم الاحتفاظ برسائل المحادثة لمدة تصل إلى 30 يوما. إذا تركت بيانات الاتصال الخاصة بك، فسيتم الاحتفاظ بها لمدة تصل إلى 18 شهرا. نحن لا نبيع بياناتك أبدا.',
    consentPolicy: 'اقرأ سياسة الخصوصية',
    consentAccept: 'ابدأ المحادثة',
    consentDecline: 'ليس الآن',
    inputPlaceholder: 'اكتب رسالتك…',
    send: 'إرسال',
    leadTitle: 'هل تريد أن يتواصل معك المالك؟',
    leadBody: 'اترك بياناتك وسيتواصل معك المالك شخصيا.',
    leadName: 'اسمك',
    leadEmail: 'البريد الإلكتروني',
    leadNote: 'هل هناك شيء محدد؟ (اختياري)',
    leadSubmit: 'إرسال إلى المالك',
    leadCancel: 'لاحقا',
    leadSuccess: 'شكرا — سيتواصل المالك معك.',
    askContact: 'اترك بياناتك',
    poweredBy: 'ردود الذكاء الاصطناعي — ليست استشارة قانونية',
    errorGeneric: 'حدث خطأ. حاول مرة أخرى.',
    ended: 'انتهت المحادثة. يرجى استخدام نموذج الاتصال.',
    quickReplies: [
      'ما الذي يوجد بالقرب؟',
      'أرني الخريطة',
      'عرض الصور',
      'هل العقار قانوني؟',
    ],
    guideChip: '📘 احصل على دليل المشتري (PDF)',
    guideCardTitle: 'تريد كل التفاصيل في PDF واحد؟',
    guideCardBody: 'احصل على دليل المشتري السريع المجاني — حقائق العقار، المسافات، الوضع القانوني وكيفية الزيارة.',
    guideFormTitle: 'دليل المشتري السريع',
    guideFormBody: 'أدخل بريدك الإلكتروني وسنرسل لك PDF. سيتم التنزيل فورا أيضا.',
    guideFormName: 'اسمك (اختياري)',
    guideFormEmail: 'البريد الإلكتروني',
    guideFormSubmit: 'أرسل لي الدليل',
    guideFormCancel: 'إلغاء',
    guideSuccess: 'تم الإرسال! تحقق من بريدك — يتم تنزيل PDF أيضا الآن.',
  },
  ru: {
    bubbleAria: 'Открыть чат',
    title: 'Консьерж Скопелос',
    subtitle: 'Задайте вопросы об объекте',
    consentTitle: 'Прежде чем начать',
    consentBody: 'Я ИИ-консьерж по коттеджам на Скопелосе. Ваши сообщения временно обрабатываются для ответов.',
    consentDetail: 'Сообщения чата хранятся до 30 дней. Если вы оставите контактные данные, они хранятся до 18 месяцев. Мы никогда не продаём ваши данные.',
    consentPolicy: 'Политика конфиденциальности',
    consentAccept: 'Начать чат',
    consentDecline: 'Не сейчас',
    inputPlaceholder: 'Ваше сообщение…',
    send: 'Отправить',
    leadTitle: 'Хотите, чтобы владелец связался?',
    leadBody: 'Оставьте контакты — владелец свяжется лично.',
    leadName: 'Ваше имя',
    leadEmail: 'Email',
    leadNote: 'Что-то конкретное? (необязательно)',
    leadSubmit: 'Отправить',
    leadCancel: 'Позже',
    leadSuccess: 'Спасибо — владелец свяжется с вами.',
    askContact: 'Оставить контакты',
    poweredBy: 'Ответы ИИ — не юридическая консультация',
    errorGeneric: 'Что-то пошло не так. Попробуйте снова.',
    ended: 'Разговор завершён. Используйте форму контакта.',
    quickReplies: [
      'Что рядом?',
      'Показать карту',
      'Посмотреть фото',
      'Юридически чисто?',
    ],
    guideChip: '📘 Получить Гид Покупателя (PDF)',
    guideCardTitle: 'Хотите всю информацию в одном PDF?',
    guideCardBody: 'Получите бесплатный Гид Покупателя — факты, расстояния, юридический статус и как осмотреть.',
    guideFormTitle: 'Быстрый Гид Покупателя',
    guideFormBody: 'Укажите email — мы пришлём PDF. Также сразу скачается.',
    guideFormName: 'Ваше имя (необязательно)',
    guideFormEmail: 'Email',
    guideFormSubmit: 'Отправить гид',
    guideFormCancel: 'Отмена',
    guideSuccess: 'Отправлено! Проверьте почту — PDF также сейчас скачивается.',
  },
};

const RTL_LANGS = ['he', 'ar'];

const ChatWidget = () => {
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;
  const isRTL = RTL_LANGS.includes(language);

  const [isOpen, setIsOpen] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // { role, content }
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', note: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [showGuideForm, setShowGuideForm] = useState(false);
  const [guideData, setGuideData] = useState({ name: '', email: '' });
  const [guideSubmitting, setGuideSubmitting] = useState(false);
  const [guideDelivered, setGuideDelivered] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [unreadDot, setUnreadDot] = useState(true);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isSending]);

  // Persist consent + session in localStorage
  useEffect(() => {
    const storedConsent = localStorage.getItem('chat_consent') === 'true';
    const storedSession = localStorage.getItem('chat_session_id');
    if (storedConsent) setHasConsented(true);
    if (storedSession) setSessionId(storedSession);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setUnreadDot(false);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Start session after consent
  const handleConsent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ language, consent: true }),
      });
      if (!res.ok) throw new Error('Failed to start chat');
      const data = await res.json();
      setSessionId(data.session_id);
      setHasConsented(true);
      setMessages([{ role: 'assistant', content: data.greeting }]);
      localStorage.setItem('chat_consent', 'true');
      localStorage.setItem('chat_session_id', data.session_id);
      // Persist consent timestamp for GDPR audit
      localStorage.setItem('chat_consent_at', new Date().toISOString());
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      toast.error(t.errorGeneric);
    }
  };

  const declineConsent = () => {
    setIsOpen(false);
  };

  // Reset session if expired (404)
  const resetSession = () => {
    localStorage.removeItem('chat_consent');
    localStorage.removeItem('chat_session_id');
    localStorage.removeItem('chat_consent_at');
    setSessionId(null);
    setHasConsented(false);
    setMessages([]);
    setSessionEnded(false);
  };

  const sendMessage = async (e, overrideText) => {
    e?.preventDefault();
    const text = (overrideText ?? input).trim();
    if (!text || isSending || sessionEnded) return;

    if (!sessionId) {
      await handleConsent();
      return;
    }

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    if (!overrideText) setInput('');
    setIsSending(true);

    try {
      const res = await fetch(`${API_URL}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: text, language }),
      });

      if (res.status === 404) {
        resetSession();
        toast.info('Session expired — please start again');
        return;
      }
      if (res.status === 429) {
        const data = await res.json();
        toast.error(data.detail || 'Too many messages. Slow down a moment.');
        setMessages(prev => prev.slice(0, -1));
        return;
      }
      if (!res.ok) throw new Error('Send failed');

      const data = await res.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      if (data.session_ended) setSessionEnded(true);
    } catch (err) {
      toast.error(t.errorGeneric);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadData.name.trim() || !leadData.email.trim()) return;
    setLeadSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: leadData.name.trim(),
          email: leadData.email.trim(),
          note: leadData.note.trim() || null,
        }),
      });
      if (!res.ok) throw new Error('Lead failed');
      toast.success(t.leadSuccess);
      setShowLeadForm(false);
      setLeadData({ name: '', email: '', note: '' });
      setMessages(prev => [...prev, { role: 'assistant', content: t.leadSuccess }]);
    } catch (err) {
      toast.error(t.errorGeneric);
    } finally {
      setLeadSubmitting(false);
    }
  };

  const submitGuide = async (e) => {
    e.preventDefault();
    if (!guideData.email.trim()) return;
    setGuideSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/guide-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          name: guideData.name.trim() || null,
          email: guideData.email.trim(),
        }),
      });
      if (!res.ok) throw new Error('Guide request failed');
      const data = await res.json();
      // Trigger instant download in a new tab
      const a = document.createElement('a');
      a.href = data.pdf_url || '/Skopelos-Buyers-Guide.pdf';
      a.download = 'Skopelos-Buyers-Guide.pdf';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      toast.success(t.guideSuccess);
      setShowGuideForm(false);
      setGuideData({ name: '', email: '' });
      setGuideDelivered(true);
      setMessages(prev => [...prev, { role: 'assistant', content: t.guideSuccess }]);
    } catch (err) {
      toast.error(t.errorGeneric);
    } finally {
      setGuideSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      {!isOpen && (
        <button
          onClick={openChat}
          aria-label={t.bubbleAria}
          data-testid="chat-bubble-open"
          className="fixed bottom-6 right-6 z-[9998] group flex items-center gap-2 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a87] hover:from-[#15293f] hover:to-[#1e3a5f] text-white shadow-2xl rounded-full pl-4 pr-5 py-3.5 transition-all duration-300 hover:scale-105 hover:shadow-[0_20px_50px_rgba(30,58,95,0.4)]"
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5" />
            {unreadDot && (
              <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#d4845f] rounded-full animate-pulse" />
            )}
          </div>
          <span className="text-sm font-medium hidden sm:inline">{t.title}</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          dir={isRTL ? 'rtl' : 'ltr'}
          className="fixed bottom-0 right-0 sm:bottom-6 sm:right-6 z-[9998] w-full sm:w-[400px] h-[100dvh] sm:h-[600px] sm:max-h-[85vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border-t sm:border border-slate-200 animate-in slide-in-from-bottom-5 duration-300"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1e3a5f] via-[#234a6e] to-[#2d5a87] text-white px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center ring-2 ring-white/20">
                <Sparkles className="w-5 h-5 text-[#f4c897]" />
              </div>
              <div>
                <h3 className="font-serif text-base leading-tight">{t.title}</h3>
                <p className="text-xs text-[#b8d4e8] leading-tight mt-0.5">{t.subtitle}</p>
              </div>
            </div>
            <button
              onClick={closeChat}
              aria-label="Close"
              data-testid="chat-close-button"
              className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Consent screen */}
          {!hasConsented ? (
            <div className="flex-1 flex flex-col p-6 bg-slate-50">
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-full flex items-center justify-center mb-5 shadow-lg">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-serif text-xl text-slate-800 mb-3">{t.consentTitle}</h4>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{t.consentBody}</p>
                <p className="text-xs text-slate-500 leading-relaxed mb-4">{t.consentDetail}</p>
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-xs text-[#1e3a5f] hover:text-[#d4845f] underline underline-offset-2 mb-6"
                  data-testid="chat-privacy-link"
                >
                  {t.consentPolicy} ↗
                </Link>
              </div>
              <div className="space-y-2 flex-shrink-0">
                <Button
                  onClick={handleConsent}
                  data-testid="chat-consent-accept"
                  className="w-full bg-[#1e3a5f] hover:bg-[#15293f] text-white py-6 rounded-xl font-medium"
                >
                  {t.consentAccept}
                </Button>
                <Button
                  onClick={declineConsent}
                  variant="ghost"
                  data-testid="chat-consent-decline"
                  className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                >
                  {t.consentDecline}
                </Button>
              </div>
            </div>
          ) : showLeadForm ? (
            // Lead form
            <form onSubmit={submitLead} className="flex-1 flex flex-col p-5 bg-slate-50" data-testid="chat-lead-form">
              <div className="mb-4">
                <h4 className="font-serif text-lg text-slate-800 mb-1">{t.leadTitle}</h4>
                <p className="text-sm text-slate-500">{t.leadBody}</p>
              </div>
              <div className="space-y-3 flex-1">
                <Input
                  type="text"
                  required
                  placeholder={t.leadName}
                  value={leadData.name}
                  onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                  data-testid="chat-lead-name"
                  className="bg-white"
                />
                <Input
                  type="email"
                  required
                  placeholder={t.leadEmail}
                  value={leadData.email}
                  onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                  data-testid="chat-lead-email"
                  className="bg-white"
                />
                <Textarea
                  placeholder={t.leadNote}
                  value={leadData.note}
                  onChange={(e) => setLeadData({ ...leadData, note: e.target.value })}
                  data-testid="chat-lead-note"
                  className="bg-white min-h-[80px] resize-none"
                  maxLength={500}
                />
              </div>
              <div className="flex gap-2 pt-3 flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowLeadForm(false)}
                  className="flex-1 text-slate-600"
                  data-testid="chat-lead-cancel"
                >
                  {t.leadCancel}
                </Button>
                <Button
                  type="submit"
                  disabled={leadSubmitting || !leadData.name.trim() || !leadData.email.trim()}
                  className="flex-1 bg-[#d4845f] hover:bg-[#c17552] text-white"
                  data-testid="chat-lead-submit"
                >
                  {leadSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.leadSubmit}
                </Button>
              </div>
            </form>
          ) : showGuideForm ? (
            // Buyer's Guide form
            <form onSubmit={submitGuide} className="flex-1 flex flex-col p-5 bg-slate-50" data-testid="chat-guide-form">
              <div className="mb-4 flex items-start gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-lg flex items-center justify-center flex-shrink-0">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-slate-800 mb-0.5 leading-tight">{t.guideFormTitle}</h4>
                  <p className="text-xs text-slate-500 leading-snug">{t.guideFormBody}</p>
                </div>
              </div>
              <div className="space-y-3 flex-1">
                <Input
                  type="text"
                  placeholder={t.guideFormName}
                  value={guideData.name}
                  onChange={(e) => setGuideData({ ...guideData, name: e.target.value })}
                  data-testid="chat-guide-name"
                  className="bg-white"
                  maxLength={100}
                />
                <Input
                  type="email"
                  required
                  placeholder={t.guideFormEmail}
                  value={guideData.email}
                  onChange={(e) => setGuideData({ ...guideData, email: e.target.value })}
                  data-testid="chat-guide-email"
                  className="bg-white"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 pt-3 flex-shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowGuideForm(false)}
                  className="flex-1 text-slate-600"
                  data-testid="chat-guide-cancel"
                >
                  {t.guideFormCancel}
                </Button>
                <Button
                  type="submit"
                  disabled={guideSubmitting || !guideData.email.trim()}
                  className="flex-1 bg-[#d4845f] hover:bg-[#c17552] text-white"
                  data-testid="chat-guide-submit"
                >
                  {guideSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      <Download className="w-4 h-4" />
                      {t.guideFormSubmit}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            // Conversation
            <>
              <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50"
                data-testid="chat-messages"
              >
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {m.role === 'assistant' && (
                      <div className="w-7 h-7 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a87] rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-auto">
                        <Sparkles className="w-3.5 h-3.5 text-[#f4c897]" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-[#1e3a5f] text-white rounded-br-sm'
                          : 'bg-white text-slate-800 shadow-sm border border-slate-100 rounded-bl-sm'
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {isSending && (
                  <div className="flex justify-start">
                    <div className="w-7 h-7 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a87] rounded-full flex items-center justify-center flex-shrink-0 mr-2">
                      <Sparkles className="w-3.5 h-3.5 text-[#f4c897]" />
                    </div>
                    <div className="bg-white shadow-sm border border-slate-100 px-4 py-3 rounded-2xl rounded-bl-sm">
                      <div className="flex gap-1.5">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick replies — shown only right after the greeting */}
                {messages.length === 1 && !isSending && !sessionEnded && t.quickReplies && (
                  <div className="flex flex-wrap gap-2 pl-9 pt-1" data-testid="chat-quick-replies">
                    {t.quickReplies.map((qr, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => sendMessage(null, qr)}
                        data-testid={`chat-quick-reply-${idx}`}
                        className="text-xs px-3 py-1.5 bg-white border border-[#1e3a5f]/20 text-[#1e3a5f] rounded-full hover:bg-[#1e3a5f] hover:text-white hover:border-[#1e3a5f] transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        {qr}
                      </button>
                    ))}
                    {!guideDelivered && (
                      <button
                        type="button"
                        onClick={() => setShowGuideForm(true)}
                        data-testid="chat-guide-chip"
                        className="text-xs px-3 py-1.5 bg-gradient-to-r from-[#d4845f] to-[#c17552] text-white border border-[#d4845f] rounded-full hover:from-[#c17552] hover:to-[#a8643f] transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                      >
                        {t.guideChip}
                      </button>
                    )}
                  </div>
                )}

                {/* Mid-conversation Buyer's Guide CTA card — shown after >=3 messages */}
                {messages.length >= 3 && !guideDelivered && !isSending && !sessionEnded && (
                  <div className="pl-9 pt-1" data-testid="chat-guide-cta-card">
                    <button
                      type="button"
                      onClick={() => setShowGuideForm(true)}
                      className="w-full text-left bg-gradient-to-br from-[#fff7f1] to-[#fceee2] border border-[#d4845f]/30 hover:border-[#d4845f] rounded-2xl p-3.5 flex items-center gap-3 transition-all duration-200 hover:shadow-md group"
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-[#d4845f] to-[#c17552] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <BookOpen className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 leading-tight">
                          {t.guideCardTitle}
                        </p>
                        <p className="text-xs text-slate-500 leading-snug mt-0.5">
                          {t.guideCardBody}
                        </p>
                      </div>
                      <Download className="w-4 h-4 text-[#d4845f] flex-shrink-0" />
                    </button>
                  </div>
                )}
              </div>

              {/* Lead capture CTA */}
              {messages.length >= 3 && !sessionEnded && (
                <div className="px-4 pb-2 flex-shrink-0 bg-slate-50">
                  <button
                    onClick={() => setShowLeadForm(true)}
                    data-testid="chat-leave-details-button"
                    className="w-full text-xs text-[#d4845f] hover:text-[#c17552] font-medium py-2 border border-dashed border-[#d4845f]/40 hover:border-[#d4845f] rounded-lg transition-colors flex items-center justify-center gap-1.5"
                  >
                    <User className="w-3.5 h-3.5" />
                    {t.askContact}
                  </button>
                </div>
              )}

              {/* Input */}
              <form onSubmit={sendMessage} className="border-t border-slate-200 p-3 bg-white flex-shrink-0">
                {sessionEnded ? (
                  <p className="text-xs text-slate-500 text-center py-2">{t.ended}</p>
                ) : (
                  <div className="flex items-end gap-2">
                    <Textarea
                      ref={inputRef}
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder={t.inputPlaceholder}
                      data-testid="chat-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage(e);
                        }
                      }}
                      disabled={isSending}
                      maxLength={1000}
                      className="flex-1 min-h-[42px] max-h-[120px] resize-none text-sm border-slate-200 focus-visible:ring-[#1e3a5f]"
                      rows={1}
                    />
                    <Button
                      type="submit"
                      disabled={isSending || !input.trim()}
                      data-testid="chat-send-button"
                      className="bg-[#1e3a5f] hover:bg-[#15293f] text-white rounded-full w-10 h-10 p-0 flex-shrink-0"
                    >
                      {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 text-center mt-2">{t.poweredBy}</p>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
